import * as THREE from "three";
import TimelineChannel from "./TimelineChannel";
import TimelineTrack, {TimelineTrackSaveObject} from "./TimelineTrack";
import Engine from "../Engine";
import {GroupsManager, ObjectGroup} from "../GroupsManager";
import ProjectSaver from "../ProjectSaver";
import {SaveModificationTypes, TimelineTrackTypes} from "../../globals";
import {assert} from "../utilities/ErrorHandling";
import ColorUtils from "../utilities/ColorUtils";
import SceneObjectUtils from "../utilities/SceneObjectUtils";
import store, {StoreActions} from "../../state/store";
import {ITimelineElementParent, TimelineElementTypes} from "../../interfaces/Timeline";
import DefaultSceneManager from "../DefaultSceneManager";
import { getUuidv4 } from "../utilities/Misc";
import {timelineElementMap} from ".";

interface TimelineObjectData {
	id: string | undefined;
	uiColor?: string;
	channels: any[];
	persistentSceneObjectId: string | undefined;
	objectId: string | undefined;
	hidden: boolean;
	objectGroupGuid?: string;
	sceneObjectGuid?: string;
}

const timelineObjectDataObject: TimelineObjectData = {
	id: undefined,
	uiColor: undefined,
	channels: [],
	persistentSceneObjectId: undefined,
	objectId: "",
	hidden: false,
	objectGroupGuid: undefined,
	sceneObjectGuid: undefined
};

export default class TimelineObject implements ITimelineElementParent
{
	#id: string;
	readonly #track: TimelineTrack;
	public channels: TimelineChannel[];
	public uiInfo: { color: string, isSelected: boolean, isExpanded: boolean, isRenaming: boolean };
	public associatedObject?: THREE.Object3D;

	// TODO this constructor is quite messy. It should be refactored, possibly into a factory?
	constructor(params: { track: TimelineTrack, hidden?: boolean, objectGroupGuid?: string, sceneObjectGuid?: string, sceneObjectReference?: THREE.Object3D, skipChannelCreation?: boolean })
	{
		this.#id = getUuidv4();
		this.#track = params.track;

		const dataObject: TimelineObjectData = {
			...timelineObjectDataObject,
			objectId: this.id,
			hidden: params.hidden ?? false,
		};
		store.dispatch(StoreActions.timeline.setElementData({ dataId: this.id, data: dataObject }));

		if (params.objectGroupGuid)
		{
			this.objectGroupGuid = params.objectGroupGuid;
			GroupsManager.linkGroupToTimelineObject(this.objectGroupGuid, this.track.timeline.associatedSceneId, this);
		}
		else if (params.sceneObjectGuid)
		{
			this.sceneObjectGuid = params.sceneObjectGuid;
			this.associatedObject = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(this.track.timeline.associatedSceneId), this.sceneObjectGuid) || undefined;
			if (this.associatedObject && this.track.type === TimelineTrackTypes.ObjectsTrack) ProjectSaver.getInstance().markModifiedObject(this.associatedObject, SaveModificationTypes.Reference);
		}
		else if (params.sceneObjectReference)
		{
			this.associatedObject = params.sceneObjectReference;
			this.sceneObjectGuid = this.associatedObject.userData.guid;
			if (this.track.type === TimelineTrackTypes.ObjectsTrack) ProjectSaver.getInstance().markModifiedObject(this.associatedObject, SaveModificationTypes.Reference);
		}
		this.channels = [];

		if (!params.skipChannelCreation)
		{
			this.channels.push(new TimelineChannel("Channel", this));
		}

		this.uiInfo = {
			color: ColorUtils.getRandomColorRGB(),
			isSelected: false,
			isExpanded: true,
			isRenaming: false,
		};

		timelineElementMap.set(this.id, this);
	}

	public getDataObject(): TimelineObjectData
	{
		return store.getState().timelineModule.elementData[this.id];
	}

	public get id(): string
	{
		return this.#id;
	}

	public get track(): TimelineTrack
	{
		return this.#track;
	}

	public get hidden(): boolean
	{
		return this.getDataObject().hidden;
	}

	public set hidden(isHidden: boolean)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "hidden", propertyValue: isHidden }));
	}

	public get objectGroupGuid(): string | undefined
	{
		return this.getDataObject().objectGroupGuid;
	}


	set objectGroupGuid(guid)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "objectGroupGuid", propertyValue: guid }));
	}

	get sceneObjectGuid()
	{
		return this.getDataObject().sceneObjectGuid;
	}

	set sceneObjectGuid(guid)
	{
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: this.id, propertyName: "sceneObjectGuid", propertyValue: guid }));
	}

	clone(newTrack: TimelineTrack, fullTimelineClone = false): TimelineObject
	{
		const params: { track: any, hidden?: boolean, objectGroupGuid?: string, sceneObjectGuid?: string } = { track: newTrack, hidden: this.hidden };

		if (this.objectGroupGuid)
		{
			params.objectGroupGuid = this.objectGroupGuid;
		}
		else if (this.associatedObject && !this.associatedObject.userData.removedFromScene)
		{
			//if the entire timeline is cloned, it means we created a new step, and we must then fetch the object from the newly copied step
			if(fullTimelineClone)
			{
				assert(SceneObjectUtils.getSceneCopyGuidMap().has(this.sceneObjectGuid!));
				params.sceneObjectGuid = SceneObjectUtils.getSceneCopyGuidMap().get(this.sceneObjectGuid!);
			}
			else //keep the same reference, which will then be validated when this object's onAdded gets called
			{
				params.sceneObjectGuid = this.sceneObjectGuid;
			}
		}

		const newObject = new TimelineObject(params);

		// clone parameters
		newObject.uiInfo = Object.assign({}, this.uiInfo);

		// clone channels
		newObject.channels = [];
		for (const oldChannel of this.channels)
		{
			newObject.channels.push(oldChannel.clone(newObject));
		}

		// return result
		return newObject;
	}

	onAdded()
	{
		//if this object references something that is already refered to by a different object - we must nullify the reference.

		if(this.objectGroupGuid)
		{
			for(const object of this.track.objects)
			{
				if(this !== object && this.objectGroupGuid === object.objectGroupGuid)
				{
					this.setReference(null);
					break;
				}
			}
		}
		else if(this.sceneObjectGuid)
		{
			//if the reference is unique, we must check if that object is a linked object.
			//if it is, check whether the current scene has a linked counterpart, and set the reference to it should it exist.
			//if no linked counterpart exists, only keep the reference if the object actually exists in the scene.

			const defaultObject = DefaultSceneManager.getInstance().getDefaultObject(this.sceneObjectGuid);
			if(defaultObject)
			{
				const linkedObject = DefaultSceneManager.getInstance().getLinkedObject(defaultObject, this.track.timeline.associatedSceneId);
				if(linkedObject)
				{
					if(linkedObject !== this.associatedObject) this.setReference(linkedObject, false);
				}
				else
				{
					this.setReference(null);
				}
			}

			if(this.sceneObjectGuid)
			{
				const existsInScene = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(this.track.timeline.associatedSceneId), this.sceneObjectGuid) !== null;
				if(!existsInScene || this.associatedObject?.userData.removedFromScene)
				{
					this.setReference(null);
				}
				else
				{
					for(const object of this.track.objects)
					{
						if(this !== object && this.sceneObjectGuid === object.sceneObjectGuid)
						{
							this.setReference(null);
							break;
						}
					}
				}
				
			}
		}

		this.getChildren().forEach((child) => child.onAdded());
	}

	save(shouldIncludeChildren: boolean): TimelineObjectData
	{
		const output: TimelineObjectData = {
			channels: [],
			objectId: "",
			persistentSceneObjectId: undefined,
			id: this.id,
			uiColor: this.uiInfo.color,
			hidden: this.hidden
		};

		if (this.objectGroupGuid)
		{
			output.objectGroupGuid = this.objectGroupGuid;
		}
		else
		{
			output.persistentSceneObjectId = this.sceneObjectGuid;
		}

		if (shouldIncludeChildren)
		{
			output.channels = [];
			this.channels.forEach((c) => output.channels.push(c.save(shouldIncludeChildren)));
		}

		return output;
	}


	load(savedData: TimelineTrackSaveObject)
	{
		store.dispatch(StoreActions.timeline.reparentElementData({ dataId: this.id, newDataId: savedData.id }));
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: savedData.id, propertyName: "objectId", propertyValue: savedData.id }));

		this.#id = savedData.id;
		this.hidden = savedData.hidden ? savedData.hidden : false;
		timelineElementMap.set(this.id, this);

		if (savedData.objectGroupGuid && GroupsManager.getGroupByGuid(savedData.objectGroupGuid))
		{
			this.objectGroupGuid = savedData.objectGroupGuid;
			GroupsManager.linkGroupToTimelineObject(this.objectGroupGuid, this.track.timeline.associatedSceneId, this);
		}
		else if (savedData.persistentSceneObjectId)
		{
			this.sceneObjectGuid = savedData.persistentSceneObjectId;
			this.associatedObject = SceneObjectUtils.findObjectByGuid(Engine.getInstance().getSelectionRoot(this.track.timeline.associatedSceneId), this.sceneObjectGuid) || undefined;
			if (this.associatedObject && this.track.type === TimelineTrackTypes.ObjectsTrack) ProjectSaver.getInstance().markModifiedObject(this.associatedObject, SaveModificationTypes.Reference);
		}
		this.uiInfo.color = savedData.uiColor;

		if (savedData.channels)
		{
			this.channels = [];
			for (let c = 0; c < savedData.channels.length; c++)
			{
				const savedChannelData = savedData.channels[c];
				const loadedChannel = new TimelineChannel("", this);
				this.channels.push(loadedChannel);
				loadedChannel.load(savedChannelData);
			}
		}
	}

	export()
	{

	}

	getSceneObjects(): THREE.Object3D[]
	{
		if (this.objectGroupGuid)
		{
			if (GroupsManager.getGroupByGuid(this.objectGroupGuid))
			{
				return GroupsManager.getGroupObjects(this.objectGroupGuid, this.track.timeline.associatedSceneId);
			}
			else return [];
		}
		else if (this.associatedObject && !this.associatedObject.userData.removedFromScene)
		{
			return [this.associatedObject];
		}
		else
		{
			return [];
		}
	}

	getType(): TimelineElementTypes
	{
		return TimelineElementTypes.Object;
	}

	getChildren(): TimelineChannel[]
	{
		return this.channels;
	}

	setChildren(children: TimelineChannel[]): void
	{
		this.channels = children;
		for (const child of children)
		{
			child.setParent(this);
		}
	}

	getParent(): TimelineTrack
	{
		return this.track;
	}

	setParent(parent: TimelineTrack): void
	{
		assert(false, "Cannot set parent of TimelineObject");
	}

	getReferenced(): ObjectGroup | THREE.Object3D | null
	{
		if (this.objectGroupGuid)
		{
			return GroupsManager.getGroupByGuid(this.objectGroupGuid) || null;
		}

		if (this.associatedObject)
		{
			return this.associatedObject;
		}

		return null;
	}

	setReference(reference: THREE.Object3D | ObjectGroup | null, callOnAdded = true)
	{
		this.onDelete();

		if (!reference)
		{
			this.associatedObject = undefined;
			this.sceneObjectGuid = undefined;
			this.objectGroupGuid = undefined;
			return;
		}

		if (reference instanceof THREE.Object3D)
		{
			this.objectGroupGuid = undefined;
			this.sceneObjectGuid = reference.userData.guid;
			this.associatedObject = reference;
			assert(this.track.type === TimelineTrackTypes.ObjectsTrack);
			ProjectSaver.getInstance().markModifiedObject(this.associatedObject, SaveModificationTypes.Reference);
		}
		else
		{
			this.associatedObject = undefined;
			this.sceneObjectGuid = undefined;
			this.objectGroupGuid = reference.guid;
		}

		if(callOnAdded) this.onAdded();
	}

	getName()
	{
		if (this.objectGroupGuid)
		{
			const group = GroupsManager.getGroupByGuid(this.objectGroupGuid);
			if (group)
			{
				return group.name;
			}
		}

		if (this.associatedObject && !this.associatedObject.userData.removedFromScene)
		{
			return this.associatedObject.name;
		}

		return "(Missing Reference)";
	}

	deleteSelf()
	{

		for (let i = this.channels.length - 1; i >= 0; i--)
		{
			this.channels[i].deleteSelf();
		}

		if (this.track)
		{
			const myIndex = this.track.objects.indexOf(this);
			if (myIndex > -1)
			{
				this.track.objects.splice(myIndex, 1);
			}
		}

		timelineElementMap.delete(this.id);
	}

	onDelete()
	{
		this.getChildren().forEach((child) => child.onDelete());
	}

}
